import http from 'hub-http/clients/apiClient';
import { createRpcClientV2 } from 'rpc-client-utils';
import { uploadDocumentRpc, getUploadProgressRpc } from '../../__generated__/chirp/com/hubspot/sales/documents/rpc/SalesDocumentsDocumentService';
const UPLOAD_PERCENT_START = 0;
const UPLOAD_PERCENT_END = 90;
const PROCESSING_PERCENT_START = UPLOAD_PERCENT_END;
const PROCESSING_PERCENT_END = 100;
const POLL_INTERVAL_MS = 1000;
const rpcClient = createRpcClientV2({
  hubHttpClient: http
});
const COMPLETED_STEP = 'COMPLETED';
const ERROR_STEP = 'ERROR';
const PROCESSING_FAILED_STEP = 'PROCESSING_FAILED';
const mapUploadProgress = (loaded, total) => {
  if (!total || total <= 0) {
    return UPLOAD_PERCENT_START;
  }
  const raw = loaded / total * UPLOAD_PERCENT_END;
  if (raw < UPLOAD_PERCENT_START) return UPLOAD_PERCENT_START;
  if (raw > UPLOAD_PERCENT_END) return UPLOAD_PERCENT_END;
  return raw;
};
const mapProcessingProgress = percentComplete => {
  // Bound percent complete to 0-100
  const bounded = Math.max(0, Math.min(100, percentComplete));
  const span = PROCESSING_PERCENT_END - PROCESSING_PERCENT_START;
  return PROCESSING_PERCENT_START + bounded / 100 * span;
};
let createXhr = () => new XMLHttpRequest();
export const __setCreateXhrForTests = factory => {
  createXhr = factory;
};
const putFileToPresignedUrl = args => {
  const {
    url,
    file,
    onProgress
  } = args;
  return new Promise((resolve, reject) => {
    const xhr = createXhr();
    xhr.open('PUT', url, true);
    if (file.type) {
      xhr.setRequestHeader('Content-Type', file.type);
    }
    xhr.upload.onprogress = event => {
      if (!onProgress) return;
      const {
        loaded,
        total
      } = event;
      onProgress(mapUploadProgress(loaded, total));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Presigned upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Presigned upload network error'));
    xhr.send(file);
  });
};
const initiateUpload = args => {
  const {
    file,
    documentId
  } = args;
  const request = {
    documentId,
    name: file.name,
    originalFileName: file.name,
    contentType: file.type || 'application/octet-stream',
    fileSizeBytes: file.size
  };
  return rpcClient.call(uploadDocumentRpc, {
    request
  });
};
const pollProcessingUntilComplete = args => {
  const {
    documentId,
    onProgress
  } = args;
  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        const request = {
          documentId
        };
        const progress = await rpcClient.call(getUploadProgressRpc, {
          request
        });
        if (onProgress) {
          onProgress(mapProcessingProgress(progress.percentComplete));
        }
        if (progress.currentStep === COMPLETED_STEP || progress.percentComplete >= 100) {
          resolve(progress);
          return;
        }
        if (progress.currentStep === ERROR_STEP || progress.currentStep === PROCESSING_FAILED_STEP) {
          reject(new Error(progress.errorMessage || 'Upload processing failed'));
          return;
        }
        setTimeout(() => {
          void poll();
        }, POLL_INTERVAL_MS);
      } catch (e) {
        reject(e);
      }
    };
    void poll();
  });
};

// TODO: add optional brandId once this is supported on CHIRP https://github.com/HubSpotEngineering/documents-ui/issues/2339
export const uploadFileCHIRP = async args => {
  const {
    file,
    onProgress,
    documentId
  } = args;
  const initiateResponse = await initiateUpload({
    file,
    documentId
  });
  const {
    documentId: createdId,
    presignedUploadUrl
  } = initiateResponse;
  const idToUse = documentId !== null && documentId !== void 0 ? documentId : createdId;
  await putFileToPresignedUrl({
    url: presignedUploadUrl,
    file,
    onProgress
  });
  const lastProgress = await pollProcessingUntilComplete({
    documentId: idToUse,
    onProgress
  });
  return {
    documentId: idToUse,
    lastProgress
  };
};
export const uploadProgressionDataCHIRP = (documentId, onProgress) => {
  return pollProcessingUntilComplete({
    documentId,
    onProgress
  });
};